import NestedFormController from "./nested_form_controller"

// Brief search-zone chips: the shared nested-form controller provides the
// template cloning and the remove/_destroy mechanics; this controller adds the
// Google Places bridge (form--google-autocomplete-input outlet), the placeData
// -> hidden fields mapping, deduplication, and the max-zones limit on the
// search input.
export default class extends NestedFormController {
  static targets = ["input", "chip", "chipLabel", "limitMessage"]
  static outlets = ["form--google-autocomplete-input"]

  chipTargetConnected() {
    this.#refresh()
  }

  formGoogleAutocompleteInputOutletConnected(outlet) {
    outlet.selectGooglePlace = this.selectGooglePlace.bind(this)
    outlet.selectDefaultSuggestion = this.selectDefaultSuggestion.bind(this)
  }

  selectGooglePlace(placeData) {
    const country = this.#findAddressComponent(placeData, "country")
    const locality = this.#findAddressComponent(placeData, "locality", "postal_town", "sublocality", "neighborhood")
    const department = this.#findAddressComponent(placeData, "administrative_area_level_2")
    const region = this.#findAddressComponent(placeData, "administrative_area_level_1")
    const isEstablishment = placeData.types?.includes("establishment")

    this.#addChip({
      // Product rule: an establishment zone keeps the establishment name; an address zone is
      // named by its most precise component, never the street.
      name: (isEstablishment && placeData.name) ||
        locality?.long_name || department?.long_name || region?.long_name || country?.long_name ||
        placeData.formatted_address,
      address: placeData.formatted_address,
      lat: placeData.geometry?.location?.lat() ?? "",
      lng: placeData.geometry?.location?.lng() ?? "",
      street: this.#findAddressComponent(placeData, "street_address", "route")?.long_name || "",
      locality: locality?.long_name || "",
      administrative_area_level_1: region?.long_name || "",
      administrative_area_level_2: department?.long_name || "",
      zipcode: this.#findAddressComponent(placeData, "postal_code")?.long_name || "",
      country: country?.long_name || "",
      country_code: country?.short_name || "",
      google_place_id: isEstablishment ? placeData.place_id : ""
    })
  }

  // Address-only pick from the default suggestions: the server geocodes the
  // missing fields through Location#fetch_geocode_data on create.
  selectDefaultSuggestion(formattedAddress) {
    this.#addChip({ name: formattedAddress.split(",")[0].trim(), address: formattedAddress })
  }

  // Persisted chips are hidden (display: none), not removed from the DOM, so
  // chipTargetDisconnected never fires for them: refresh explicitly.
  remove(event) {
    super.remove(event)
    this.#refresh()
  }

  // Private

  #addChip(attributes) {
    this.inputTarget.value = ""
    if (this.#activeChips().length >= this.maxValue) return
    if (this.#duplicate(attributes)) return

    this.add(new Event("event-locations:add"))
    this.#fillChip(this.chipTargets.at(-1), attributes)
  }

  #fillChip(chip, attributes) {
    Object.entries(attributes).forEach(([field, value]) => {
      const input = chip.querySelector(`input[name$='[${field}]']`)
      if (input) input.value = value
    })
    this.chipLabelTargets.find(label => chip.contains(label)).textContent = attributes.name || attributes.address
    chip.dataset.address = attributes.address || ""
    chip.dataset.googlePlaceId = attributes.google_place_id || ""
  }

  #refresh() {
    const limitReached = this.#activeChips().length >= this.maxValue

    this.inputTarget.disabled = limitReached
    if (this.hasLimitMessageTarget) this.limitMessageTarget.classList.toggle("d-none", !limitReached)
  }

  #activeChips() {
    return this.chipTargets.filter(chip => chip.style.display !== "none")
  }

  #duplicate({ google_place_id, address }) {
    return this.#activeChips().find(chip =>
      (google_place_id && chip.dataset.googlePlaceId === google_place_id) ||
      chip.dataset.address?.toLowerCase() === address?.toLowerCase()
    )
  }

  #findAddressComponent(placeData, ...types) {
    return placeData.address_components?.find(component => types.some(type => component.types.includes(type)))
  }
}
